import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TenantLayout from '../components/TenantLayout';
import { API_BASE_URL } from '../config/api';

const MonthlyPassManagement = () => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPass, setSelectedPass] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const statusOptions = [
    { key: 'all', label: 'All Passes', color: '#6b7280' },
    { key: 'active', label: 'Active', color: '#10b981' },
    { key: 'expired', label: 'Expired', color: '#ef4444' },
    { key: 'pending', label: 'Pending Verification', color: '#f59e0b' }
  ];

  useEffect(() => {
    fetchPasses();
  }, []);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API_BASE_URL}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        },
        params: {
          category: 'pass_ticket'
        }
      });
      setPasses(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching passes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePassStatus = async (passId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API_BASE_URL}/api/bookings/${passId}/status`, 
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': user.tenantId
          }
        }
      );
      fetchPasses();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error updating pass status:', error);
      alert('Error updating pass status');
    }
  };

  const getPassStatus = (pass) => {
    const now = new Date();
    const expiryDate = new Date(pass.createdAt);
    expiryDate.setDate(expiryDate.getDate() + (pass.ticketDetails?.validity?.duration || 30));
    
    if (pass.status === 'pending') return 'pending';
    if (now > expiryDate) return 'expired';
    return 'active';
  };

  const filteredPasses = passes.filter(pass => {
    const matchesStatus = selectedStatus === 'all' || getPassStatus(pass) === selectedStatus;
    const matchesSearch = (pass.customerDetails?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (pass.customerDetails?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (pass.bookingId || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getExpiryDate = (pass) => {
    const startDate = new Date(pass.createdAt);
    const validityDays = pass.ticketDetails?.validity?.duration || 30;
    const expiryDate = new Date(startDate);
    expiryDate.setDate(expiryDate.getDate() + validityDays);
    return expiryDate;
  };

  const getRemainingDays = (pass) => {
    const now = new Date();
    const expiryDate = getExpiryDate(pass);
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (loading) {
    return (
      <TenantLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <div style={{ fontSize: '18px' }}>Loading passes...</div>
        </div>
      </TenantLayout>
    );
  }

  return (
    <TenantLayout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Monthly Pass Management</h1>
          <p style={{ color: '#718096', margin: 0 }}>Manage all pass bookings and verifications</p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {statusOptions.map(status => (
              <button
                key={status.key}
                onClick={() => setSelectedStatus(status.key)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  background: selectedStatus === status.key ? status.color : '#f3f4f6',
                  color: selectedStatus === status.key ? 'white' : '#374151',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {status.label}
              </button>
            ))}
          </div>
          
          <input
            type="text"
            placeholder="Search by name, email, or booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '300px'
            }}
          />
        </div>

        {/* Passes Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {filteredPasses.map(pass => {
            const status = getPassStatus(pass);
            const statusInfo = statusOptions.find(s => s.key === status);
            const remainingDays = getRemainingDays(pass);
            
            return (
              <div key={pass._id} style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: `linear-gradient(135deg, ${statusInfo.color}, ${statusInfo.color}dd)`,
                  color: 'white',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>{statusInfo.label}</span>
                    <span style={{ fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                      {pass.ticketDetails?.validity?.duration || 30} Days Pass
                    </span>
                  </div>
                  <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '700' }}>{pass.customerDetails?.name || 'N/A'}</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>{pass.customerDetails?.email || 'N/A'}</p>
                </div>
                
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Booking ID</label>
                      <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>{pass.bookingId}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Amount</label>
                      <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#10b981' }}>₹{pass.totalAmount}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Issue Date</label>
                      <p style={{ margin: '2px 0', fontSize: '14px', color: '#374151' }}>{formatDate(pass.createdAt)}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Expires On</label>
                      <p style={{ margin: '2px 0', fontSize: '14px', color: '#374151' }}>{formatDate(getExpiryDate(pass))}</p>
                    </div>
                  </div>
                  
                  {/* Slot Information */}
                  {pass.selectedSlot && (
                    <div style={{
                      background: '#fef7e0',
                      border: '1px solid #f6cc02',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>⏰</span>
                        <div>
                          <label style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>Time Slot</label>
                          <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                            {pass.selectedSlot.startTime} - {pass.selectedSlot.endTime}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {status === 'active' && (
                    <div style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#15803d' }}>{remainingDays}</div>
                      <div style={{ fontSize: '12px', color: '#166534' }}>Days Remaining</div>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setSelectedPass(pass);
                        setShowDetailsModal(true);
                      }}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                        color: 'white',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      View Details
                    </button>
                    
                    {status === 'pending' && (
                      <button
                        onClick={() => updatePassStatus(pass._id, 'confirmed')}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPasses.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎫</div>
            <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>No Passes Found</h3>
            <p style={{ color: '#718096' }}>
              {searchTerm ? 'No passes match your search criteria' : 'No pass bookings have been made yet'}
            </p>
          </div>
        )}

        {/* Pass Details Modal */}
        {showDetailsModal && selectedPass && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#2d3748' }}>Pass Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>Customer Name</label>
                  <p style={{ margin: '4px 0', fontSize: '16px', fontWeight: '600' }}>{selectedPass.customerDetails?.name || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>Email</label>
                  <p style={{ margin: '4px 0', fontSize: '16px' }}>{selectedPass.customerDetails?.email || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>Phone</label>
                  <p style={{ margin: '4px 0', fontSize: '16px' }}>{selectedPass.customerDetails?.phone || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>Pass Type</label>
                  <p style={{ margin: '4px 0', fontSize: '16px' }}>
                    {selectedPass.ticketDetails?.passTicketSettings?.passType === 'limited' 
                      ? `Limited (${selectedPass.ticketDetails?.passTicketSettings?.visitLimit || 10} visits)`
                      : 'Unlimited Visits'
                    }
                  </p>
                </div>
              </div>
              
              {/* Slot Information in Modal */}
              {selectedPass.selectedSlot && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#374151' }}>Time Slot Assignment</h3>
                  <div style={{
                    background: '#fef7e0',
                    border: '1px solid #f6cc02',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>⏰</span>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#92400e' }}>
                        {selectedPass.selectedSlot.startTime} - {selectedPass.selectedSlot.endTime}
                      </div>
                      <div style={{ fontSize: '14px', color: '#92400e', marginTop: '4px' }}>
                        Daily time slot for park visits
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Document Images */}
              {(selectedPass.documents?.passportPhoto || selectedPass.documents?.idProof) && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#374151' }}>Uploaded Documents</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    {selectedPass.documents?.passportPhoto && (
                      <div>
                        <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>📷 Passport Photo</label>
                        <img 
                          src={selectedPass.documents.passportPhoto} 
                          alt="Passport Photo"
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                      </div>
                    )}
                    {selectedPass.documents?.idProof && (
                      <div>
                        <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>🆔 ID Proof</label>
                        <img 
                          src={selectedPass.documents.idProof} 
                          alt="ID Proof"
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
                {getPassStatus(selectedPass) === 'pending' && (
                  <button
                    onClick={() => updatePassStatus(selectedPass._id, 'confirmed')}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: '#10b981',
                      color: 'white',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Approve Pass
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </TenantLayout>
  );
};

export default MonthlyPassManagement;